import { DealCard } from "@/components/DealCard";
import { CourseCard } from "@/components/CourseCard";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Product } from "@/types/product";

interface BrowseProductGridProps {
  products: Product[];
  isEducation: boolean;
  isLoading: boolean;
  error: Error | null;
  isAdmin: boolean;
  visibleCount: number;
  totalCount: number;
  loadMoreRef: React.RefObject<HTMLDivElement>;
  onRefetch: () => void;
  onClearFilters: () => void;
}

export function BrowseProductGrid({
  products,
  isEducation,
  isLoading,
  error,
  isAdmin,
  visibleCount,
  totalCount,
  loadMoreRef,
  onRefetch,
  onClearFilters,
}: BrowseProductGridProps) {
  const navigate = useNavigate();
  const displayProducts = products.slice(0, visibleCount);

  return (
    <>
      <div className={`grid gap-6 ${
        isEducation 
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
          : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
      }`}>
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
        ) : error ? (
          <div className="col-span-full text-center py-12">
            <p className="text-destructive mb-4">Failed to load products</p>
            <Button variant="outline" onClick={onRefetch}>
              Retry
            </Button>
          </div>
        ) : displayProducts.length === 0 ? (
          <div className="col-span-full text-center py-12 space-y-4">
            <p className="text-muted-foreground mb-4">No products found matching your filters</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button variant="outline" onClick={onClearFilters}>
                Clear all filters
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/ask-circle")}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                <span className="flex items-baseline gap-1">
                  <span>Get help from Ask</span>
                  <span className="font-handelson font-light text-base -ml-0.5">Circle</span>
                </span>
              </Button>
            </div>
          </div>
        ) : (
          displayProducts.map((product) => 
            isEducation ? (
              <CourseCard
                key={product.id}
                id={product.id}
                slug={product.slug}
                title={product.title}
                subtitle={product.subtitle}
                image={product.image}
                price={product.price}
                msrp={product.msrp || product.price}
                price_duration={product.price_duration}
                rating={product.rating}
                review_count={product.review_count}
                categories={Array.isArray(product.categories) ? product.categories.map(c => String(c)) : []}
                instructor_info={typeof product.instructor_info === 'object' && product.instructor_info !== null ? product.instructor_info as any : undefined}
                profile_image={product.profile_image}
                total_video_hours={product.total_video_hours}
                certificate_included={product.certificate_included}
                student_count={product.student_count}
                preview_lesson_url={(product as any).preview_lesson_url}
                skill_level={product.skill_level}
                isAdmin={isAdmin}
                is_published={product.is_published}
                isFromDatabase={true}
                is_hot={product.is_hot}
                sort_order={product.sort_order}
                isFavorited={(product as any).isFavorited || false}
              />
            ) : (
              <DealCard 
                key={product.id}
                id={product.id}
                slug={product.slug}
                title={product.title}
                subtitle={product.subtitle}
                image={product.image}
                price={product.price}
                msrp={product.msrp || product.price}
                price_duration={product.price_duration}
                rating={product.rating}
                review_count={product.review_count}
                is_select={product.is_select}
                categories={Array.isArray(product.categories) ? product.categories.map(c => String(c)) : []}
                plansCount={product.plansCount}
                startingPrice={product.startingPrice}
                isAdmin={isAdmin}
                is_published={product.is_published}
                isFromDatabase={true}
                is_hot={product.is_hot}
                isFavorited={(product as any).isFavorited || false}
                circleScore={product.circleScore || null}
                sort_order={product.sort_order}
              />
            )
          )
        )}
      </div>

      {/* Infinite scroll trigger */}
      {displayProducts.length < totalCount && (
        <div 
          ref={loadMoreRef} 
          className="col-span-full py-8 text-center"
        >
          <div className="text-sm text-muted-foreground">
            Loading more products...
          </div>
        </div>
      )}
    </>
  );
}
